import React, {
  ChangeEvent,
  FocusEvent,
  FocusEventHandler,
  KeyboardEvent,
  useEffect,
  useState,
} from 'react';
import { DefaultTheme, defaultTheme } from 'types/search';
import Results, { Item } from './Results';
import SearchInput from './SearchInput';

import debounce from 'lodash/debounce';
import filter from 'lodash/filter';
import includes from 'lodash/includes';
import toLower from 'lodash/toLower';

export const DEFAULT_INPUT_DEBOUNCE = 200;
export const MAX_RESULTS = 10;

// Define default search options (replacing Fuse options)
const defaultSearchOptions = {
  keys: ['name'],
  threshold: 0.6,
  ignoreCase: true
};

export interface SearchAutocompleteProps<T> {
  items: T[];
  searchOptions?: {
    keys: string[];
    threshold?: number;
    ignoreCase?: boolean;
  };
  inputDebounce?: number;
  onSearch?: (keyword: string, results: T[]) => void;
  onHover?: (result: T) => void;
  onSelect?: (result: T) => void;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  showIcon?: boolean;
  maxResults?: number;
  placeholder?: string;
  autoFocus?: boolean;
  styling?: DefaultTheme;
  resultStringKeyName?: string;
  inputSearchString?: string;
  formatResult?: Function;
  showNoResults?: boolean;
  showNoResultsText?: string;
  showItemsOnFocus?: boolean;
  maxLength?: number;
  className?: string;
}

export default function SearchAutocomplete<T>({
  items = [],
  searchOptions = defaultSearchOptions,
  inputDebounce = DEFAULT_INPUT_DEBOUNCE,
  onSearch = () => { },
  onHover = () => { },
  onSelect = () => { },
  onFocus = () => { },
  showIcon = true,
  maxResults = MAX_RESULTS,
  placeholder = '',
  autoFocus = false,
  styling = {},
  resultStringKeyName = 'name',
  inputSearchString = '',
  formatResult,
  showNoResults = true,
  showNoResultsText = 'No results',
  showItemsOnFocus = false,
  maxLength = 0,
  className,
}: SearchAutocompleteProps<T>) {
  const theme = { ...defaultTheme, ...styling };
  const options = { ...defaultSearchOptions, ...searchOptions };

  const [searchString, setSearchString] = useState<string>(inputSearchString);
  const [results, setResults] = useState<any[]>([]);
  const [highlightedItem, setHighlightedItem] = useState<number>(-1);
  const [isSearchComplete, setIsSearchComplete] = useState<boolean>(false);
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [showNoResultsFlag, setShowNoResultsFlag] = useState<boolean>(false);
  const [hasFocus, setHasFocus] = useState<boolean>(false);

  useEffect(() => {
    setSearchString(inputSearchString);
    const timeoutId = setTimeout(() => setResults(searchResults(inputSearchString)), 0);

    return () => clearTimeout(timeoutId);
  }, [inputSearchString]);

  useEffect(() => {
    setResults(searchResults(searchString));
  }, [items]);

  useEffect(() => {
    if (
      showNoResults &&
      searchString.length > 0 &&
      !isTyping &&
      results.length === 0 &&
      !isSearchComplete
    ) {
      setShowNoResultsFlag(true);
    } else {
      setShowNoResultsFlag(false);
    }
  }, [isTyping, showNoResults, isSearchComplete, searchString, results]);

  useEffect(() => {
    if (showItemsOnFocus && results.length === 0 && searchString.length === 0 && hasFocus) {
      setResults(items.slice(0, maxResults));
    }
  }, [showItemsOnFocus, results, searchString, hasFocus]);

  useEffect(() => {
    const handleDocumentClick = () => {
      eraseResults();
      setHasFocus(false);
    };

    document.addEventListener('click', handleDocumentClick);

    return () => document.removeEventListener('click', handleDocumentClick);
  }, []);

  const handleOnFocus = (event: FocusEvent<HTMLInputElement>) => {
    onFocus(event);
    setHasFocus(true);
  };

  const searchResults = (keyword: string): T[] => {
    if (!keyword || keyword.length === 0) {
      return [];
    }

    const searchTerm = options.ignoreCase ? toLower(keyword) : keyword;
    const searchKeys = options.keys || [resultStringKeyName];

    return filter(items, (item: any) => {
      return searchKeys.some(key => {
        const value = item[key];
        if (!value) return false;

        const strValue = options.ignoreCase ? toLower(String(value)) : String(value);
        return includes(strValue, searchTerm);
      });
    }).slice(0, maxResults);
  };

  const callOnSearch = (keyword: string) => {
    let newResults: T[] = [];

    keyword?.length > 0 && (newResults = searchResults(keyword));

    setResults(newResults);
    onSearch(keyword, newResults);
    setIsTyping(false);
  };

  const handleOnSearch = React.useCallback(
    inputDebounce > 0
      ? debounce((keyword: string) => callOnSearch(keyword), inputDebounce)
      : (keyword: string) => callOnSearch(keyword),
    [items]
  );

  const handleOnClick = (result: Item<T>) => {
    eraseResults();
    onSelect(result);
    setSearchString(result[resultStringKeyName]);
    setHighlightedItem(0);
  };

  const handleSetSearchString = ({ target }: ChangeEvent<HTMLInputElement>) => {
    const keyword = target.value;

    setSearchString(keyword);
    handleOnSearch(keyword);
    setIsTyping(true);

    if (isSearchComplete) {
      setIsSearchComplete(false);
    }
  };

  const eraseResults = () => {
    setResults([]);
    setIsSearchComplete(true);
  };

  const handleSetHighlightedItem = ({
    index,
    event,
  }: {
    index?: number;
    event?: KeyboardEvent<HTMLInputElement>;
  }) => {
    let itemIndex = -1;

    const setValues = (index: number) => {
      setHighlightedItem(index);
      results?.[index] && onHover(results[index]);
    };

    if (index !== undefined) {
      setHighlightedItem(index);
      results?.[index] && onHover(results[index]);
    } else if (event) {
      switch (event.key) {
        case 'Enter':
          if (results.length > 0 && results[highlightedItem]) {
            event.preventDefault();
            onSelect(results[highlightedItem]);
            setSearchString(results[highlightedItem][resultStringKeyName]);
            onSearch(results[highlightedItem][resultStringKeyName], results);
          } else {
            onSearch(searchString, results);
          }
          setHighlightedItem(-1);
          eraseResults();
          break;
        case 'ArrowUp':
          event.preventDefault();
          itemIndex = highlightedItem > -1 ? highlightedItem - 1 : results.length - 1;
          setValues(itemIndex);
          break;
        case 'ArrowDown':
          event.preventDefault();
          itemIndex = highlightedItem < results.length - 1 ? highlightedItem + 1 : -1;
          setValues(itemIndex);
          break;
        default:
          break;
      }
    }
  };

  return (
    <div className={`search-autocomplete ${className}`}>
      <div className="wrapper">
        <SearchInput
          searchString={searchString}
          setSearchString={handleSetSearchString}
          eraseResults={eraseResults}
          autoFocus={autoFocus}
          onFocus={handleOnFocus}
          placeholder={placeholder}
          showIcon={showIcon}
          theme={theme}
          setHighlightedItem={handleSetHighlightedItem}
          maxLength={maxLength}
        />
        <Results
          results={results}
          onClick={handleOnClick}
          setSearchString={setSearchString}
          showIcon={showIcon}
          maxResults={maxResults}
          resultStringKeyName={resultStringKeyName}
          formatResult={formatResult}
          theme={theme}
          highlightedItem={highlightedItem}
          setHighlightedItem={handleSetHighlightedItem}
          showNoResultsFlag={showNoResultsFlag}
          showNoResultsText={showNoResultsText}
        />
      </div>

      {/* Styled JSX block for local component styles */}
      <style jsx>{`
        .search-autocomplete {
          position: relative;
          height: ${theme.height};
        }
        .wrapper {
          position: absolute;
          display: flex;
          flex-direction: column;
          width: 100%;
          border: red;
          border-radius: ${theme.borderRadius};
          background-color: ${theme.backgroundColor};
          color: ${theme.color};
          font-size: ${theme.fontSize};
          font-family: ${theme.fontFamily};
          z-index: ${theme.zIndex};
        }
        .wrapper:hover,
        .wrapper:active,
        .wrapper:focus-within {
          box-shadow: ${theme.boxShadow};
        }
      `}
      </style>
    </div>
  );
}
